import { Component, Input, ElementRef, OnInit, OnChanges } from "@angular/core";
import { Store } from "@ngrx/store";
import { Signature } from "../../store/document/state";
import { EsignService } from "./esign.service";
import { RootState } from "../../store/root";
import * as Document from "../../store/document/actions";

const SIGNATURE_DPI = 288;

@Component({
    selector: "esignature",
    template: "<div>Loading...</div><br>",
})
export class Esignature implements OnInit {
    @Input() field: Signature;
    @Input() signatureScale: number;
    @Input() store: Store<RootState>;
    canvas: any;
    ctx: any;

    drawing: boolean;
    lastX: number;
    lastY: number;
    changed: boolean;

    constructor(
        private canvasContainer: ElementRef,
        private esignService: EsignService,
    ) {}

    initCanvas(): void {
        this.drawing = false;
        this.changed = false;

        while (this.canvasContainer.nativeElement.hasChildNodes()) {
            this.canvasContainer.nativeElement.removeChild(
                this.canvasContainer.nativeElement.lastChild,
            );
        }
        this.canvas = document.createElement("canvas");
        this.canvasContainer.nativeElement.appendChild(this.canvas);

        this.canvas.addEventListener("mousedown", (event) =>
            this.mousedown(event),
        );
        this.canvas.addEventListener("mousemove", (event) =>
            this.mousemove(event),
        );
        this.canvas.addEventListener("mouseup", (event) => this.mouseup(event));
        this.canvas.addEventListener("touchstart", (event) =>
            this.touchstart(event),
        );
        this.canvas.addEventListener("touchmove", (event) =>
            this.touchmove(event),
        );
        this.canvas.addEventListener("touchend", (event) =>
            this.mouseup(event),
        );

        const { height: canvasHeight, width: canvasWidth } = this.canvasSize;
        const { height: elementHeight, width: elementWidth } = this.elementSize;

        this.canvas.height = canvasHeight;
        this.canvas.width = canvasWidth;

        this.canvas.style.height = elementHeight + "px";
        this.canvas.style.width = elementWidth + "px";
        this.canvas.style.backgroundColor = "#ffffff";
        this.canvas.style.border = "1px solid #555555";

        this.ctx = this.canvas.getContext("2d");
        this.ctx.scale(this.resolutionFactor, this.resolutionFactor);

        window.addEventListener("resize", this.resizeCanvas, false);
    }

    ngOnInit(): void {
        this.initCanvas();
    }

    mousedown(event): void {
        if (event.offsetX !== undefined) {
            this.lastX = event.offsetX;
            this.lastY = event.offsetY;
        } else {
            // Firefox compatibility
            this.lastX = event.layerX - event.currentTarget.offsetLeft;
            this.lastY = event.layerY - event.currentTarget.offsetTop;
        }

        // begins new line
        this.ctx.beginPath();

        this.drawing = true;
    }

    mousemove(event): void {
        event.preventDefault();
        if (this.drawing) {
            // get current mouse position
            let currentY, currentX;
            if (event.offsetX !== undefined) {
                currentX = event.offsetX;
                currentY = event.offsetY;
            } else {
                currentX = event.layerX - event.currentTarget.offsetLeft;
                currentY = event.layerY - event.currentTarget.offsetTop;
            }

            this.draw(this.lastX, this.lastY, currentX, currentY);

            // set current coordinates to last one
            this.lastX = currentX;
            this.lastY = currentY;
        }
    }

    mouseup(event): void {
        this.drawing = false;
        this.changed = true;
        if (this.store) {
            this.store.dispatch(
                new Document.SetSignature(
                    this.field.index,
                    this.getSignatureImage(),
                ),
            );
        }
    }

    touchstart(event): void {
        event.preventDefault();
        event.stopPropagation();
        if (event.offsetX !== undefined) {
            this.lastX = event.offsetX;
            this.lastY = event.offsetY;
        } else {
            // Firefox compatibility
            let offsetLeft = event.target.offsetLeft;
            let offsetTop = event.target.offsetTop;
            let offsetParent = event.target.offsetParent;
            while (offsetParent !== null) {
                offsetLeft += offsetParent.offsetLeft;
                offsetTop += offsetParent.offsetTop;
                offsetParent = offsetParent.offsetParent;
            }

            this.lastX = event.targetTouches[0].clientX - offsetLeft;
            this.lastY = event.targetTouches[0].clientY - offsetTop;
        }

        // begins new line
        this.ctx.beginPath();

        this.drawing = true;
    }

    touchmove(event): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.drawing) {
            // get current mouse position
            let currentY, currentX;
            if (event.offsetX !== undefined) {
                currentX = event.offsetX;
                currentY = event.offsetY;
            } else {
                let offsetLeft = event.target.offsetLeft;
                let offsetTop = event.target.offsetTop;
                let offsetParent = event.target.offsetParent;

                while (offsetParent !== null) {
                    offsetLeft += offsetParent.offsetLeft;
                    offsetTop += offsetParent.offsetTop;
                    offsetParent = offsetParent.offsetParent;
                }

                currentX = event.targetTouches[0].clientX - offsetLeft;
                currentY = event.targetTouches[0].clientY - offsetTop;
            }

            this.draw(this.lastX, this.lastY, currentX, currentY);

            // set current coordinates to last one
            this.lastX = currentX;
            this.lastY = currentY;
        }
    }

    reset(): void {
        this.canvas.width = this.canvas.width;
    }

    draw(lX, lY, cX, cY): void {
        // line from
        this.ctx.moveTo(lX, lY);
        // to
        this.ctx.lineTo(cX, cY);
        // color
        this.ctx.strokeStyle = "#000000";
        this.ctx.lineWidth = 1;
        this.ctx.lineJoin = "round";
        this.ctx.shadowBlur = 1;
        this.ctx.shadowColor = "#111111";
        // draw it
        this.ctx.stroke();
    }

    setSignatureImage(dataUrl: string): void {
        this.canvas.width = this.canvas.width;
        const tmpImg = new Image();
        tmpImg.onload = () => {
            this.ctx.drawImage(
                tmpImg,
                0,
                0,
                this.canvas.clientWidth,
                this.canvas.clientHeight,
            );
        };
        tmpImg.src = dataUrl;
    }

    getSignatureImage(): string {
        const tmpCanvas = document.createElement("canvas");
        const { height, width } = this.canvasSize;
        tmpCanvas.width = width;
        tmpCanvas.height = height;

        const tmpCtx = tmpCanvas.getContext("2d");
        tmpCtx.drawImage(this.canvas, 0, 0, width, height);

        return tmpCanvas.toDataURL();
    }

    resizeCanvas = () => {
        const ratio = this.canvas.width / this.canvas.height;
        const tmpPic = this.canvas.toDataURL();
        this.canvas.width = this.canvas.clientWidth;
        this.canvas.height = this.canvas.clientWidth / ratio;
        const tmpImg = new Image();
        tmpImg.onload = () => {
            this.ctx.drawImage(tmpImg, 0, 0);
        };
        tmpImg.src = tmpPic;
    };

    get elementSize(): Size {
        const height = Math.ceil(this.field.height * this.signatureScale);
        const width = Math.ceil(this.field.width * this.signatureScale);
        return { height, width };
    }

    get resolutionFactor(): number {
        return SIGNATURE_DPI / 96; // default DPI is 96
    }

    get canvasSize(): Size {
        const { height: elementHeight, width: elementWidth } = this.elementSize;
        const height = Math.ceil(elementHeight * this.resolutionFactor);
        const width = Math.ceil(elementWidth * this.resolutionFactor);
        return { height, width };
    }
}

interface Size {
    height: number;
    width: number;
}
